import React, { useState, useEffect } from "react";
import { useUserRole } from "../hooks/useUserRole";
import { QRCodeDisplay } from "../components/QRCodeDisplay";
import {
  getUserProfile,
  generateQRCode,
  getUserActivityLog,
  formatDate,
  formatTransactionType,
  type UserProfile,
  type QRCodeData,
} from "../services/userApi";
import { LedgerTransaction } from "../types";
import "../styles/UserProfile.css";

export function UserProfilePage() {
  const { user, loading: authLoading } = useUserRole();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [qrCode, setQRCode] = useState<QRCodeData | null>(null);
  const [activities, setActivities] = useState<LedgerTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [qrLoading, setQRLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");

  // Load user profile and activity
  useEffect(() => {
    if (!authLoading && user) {
      loadProfileData();
    }
  }, [authLoading, user]);

  const loadProfileData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user profile
      const userProfile = await getUserProfile(user!.uid);
      setProfile(userProfile);
      setDisplayName(userProfile.displayName);
      setPhone(userProfile.phone || "");

      // Get activity log
      const activityLog = await getUserActivityLog(user!.uid, 15);
      setActivities(activityLog);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load profile");
      console.error("Error loading profile:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQR = async () => {
    try {
      setQRLoading(true);
      setError(null);
      const qr = await generateQRCode(user!.uid);
      setQRCode(qr);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to generate QR code");
      console.error("Error generating QR:", err);
    } finally {
      setQRLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    // TODO: Implement profile update API call
    setEditMode(false);
  };

  if (authLoading || loading) {
    return (
      <div className="page-container">
        <div className="content" style={{ textAlign: "center", padding: "60px 30px" }}>
          <div className="spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="page-container">
        <div className="content">
          <h1>👤 User Profile</h1>
          <p style={{ color: "red" }}>Unable to load profile. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="content">
        <h1>👤 User Profile</h1>

        {error && <div className="error-message">{error}</div>}

        {/* Profile Header Card */}
        <div className="profile-header-card">
          <div className="profile-avatar">
            {profile.displayName?.charAt(0).toUpperCase() || "U"}
          </div>
          <div className="profile-info">
            <div>
              <h2>{profile.displayName}</h2>
              <p className="role-badge">{profile.role}</p>
            </div>
            <p className="email-text">{profile.email}</p>
          </div>
        </div>

        {/* Account Details Section */}
        <div className="section">
          <h3>Account Details</h3>
          {editMode ? (
            <div className="form-section">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Enter your phone number"
                />
              </div>
              <div className="form-actions">
                <button className="btn btn-save" onClick={handleSaveProfile}>
                  ✓ Save
                </button>
                <button className="btn btn-cancel" onClick={() => setEditMode(false)}>
                  ✕ Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="details-grid">
              <div className="detail-item">
                <label>Email</label>
                <span>{profile.email}</span>
              </div>
              <div className="detail-item">
                <label>Full Name</label>
                <span>{profile.displayName || "Not set"}</span>
              </div>
              <div className="detail-item">
                <label>Phone</label>
                <span>{profile.phone || "Not set"}</span>
              </div>
              <div className="detail-item">
                <label>External ID</label>
                <span>{profile.externalAccountId || "Not set"}</span>
              </div>
              <div className="detail-item">
                <label>Account Created</label>
                <span>{formatDate(profile.createdAt)}</span>
              </div>
              <div className="detail-item">
                <label>Last Updated</label>
                <span>{formatDate(profile.updatedAt)}</span>
              </div>
            </div>
          )}

          {!editMode && (
            <button className="btn btn-secondary" onClick={() => setEditMode(true)}>
              ✏️ Edit Profile
            </button>
          )}
        </div>

        {/* QR Code Section */}
        <div className="section">
          <h3>Account QR Code</h3>
          {qrCode ? (
            <QRCodeDisplay
              qrImage={qrCode.qrImage}
              encodedData={qrCode.encodedData}
              userId={user!.uid}
              onRegenerate={handleGenerateQR}
            />
          ) : (
            <div className="qr-placeholder">
              <p>No QR code generated yet.</p>
              <p className="subtitle">
                Generate a unique QR code that can be scanned for quick account access and payments.
              </p>
              <button className="btn btn-primary" onClick={handleGenerateQR} disabled={qrLoading}>
                {qrLoading ? "🔄 Generating..." : "📱 Generate QR Code"}
              </button>
            </div>
          )}
        </div>

        {/* Activity Log Section */}
        <div className="section">
          <h3>Recent Activity</h3>
          {activities.length > 0 ? (
            <div className="activity-log">
              {activities.map((activity) => (
                <div key={activity.txId} className="activity-item">
                  <div className="activity-type">
                    {formatTransactionType(activity.type)}
                  </div>
                  <div className="activity-details">
                    <div className="activity-reason">{activity.reason}</div>
                    <div className="activity-date">{formatDate(activity.createdAt)}</div>
                  </div>
                  <div className="activity-amount">
                    {activity.amount && activity.amount > 0 ? "+" : ""}{activity.amount}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="empty-state">No transaction history yet.</p>
          )}
        </div>

        {/* Account Actions */}
        <div className="section">
          <h3>Account Actions</h3>
          <div className="actions-grid">
            <button className="btn btn-secondary">🔐 Change Password</button>
            <button className="btn btn-secondary">🔗 Link External Account</button>
            <button className="btn btn-secondary">📝 View Audit Log</button>
          </div>
          <p className="note-text">
            ℹ️ Additional account management features coming soon.
          </p>
        </div>
      </div>
    </div>
  );
}
